/**
 * Wallet top-up (Odoo): `wallet/topup/prepare` → HyperPay → `wallet/topup/confirm`.
 * Prepare returns a `topup_id`; confirm credits the wallet against it once
 * HyperPay reports success (a repeat confirm answers "already processed").
 */

export type WalletTopUpMethod = "card" | "mada";

/** Odoo's `payment_brand` for each method we offer. */
export const WALLET_TOPUP_BRAND: Record<WalletTopUpMethod, string> = {
  card: "visa",
  mada: "mada",
};

export type PendingWalletTopUp = {
  amount: number;
  method: WalletTopUpMethod;
  topupId: number;
};

const PENDING_STORAGE_KEY = "ac_pending_wallet_topup";

/** The gateway redirect reloads the app, so the pending top-up outlives it here. */
export function rememberPendingWalletTopUp(pending: PendingWalletTopUp): void {
  try {
    sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(pending));
  } catch {
    /* private mode or full storage: the return page just reports failure */
  }
}

export function readPendingWalletTopUp(): PendingWalletTopUp | null {
  try {
    const raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PendingWalletTopUp>;
    const amount = Number(parsed?.amount);
    if (!Number.isFinite(amount) || amount <= 0) return null;

    const method = parsed?.method;
    if (method !== "card" && method !== "mada") return null;

    const topupId = Number(parsed?.topupId);
    if (!Number.isInteger(topupId) || topupId <= 0) return null;

    return { amount, method, topupId };
  } catch {
    return null;
  }
}

export function clearPendingWalletTopUp(): void {
  try {
    sessionStorage.removeItem(PENDING_STORAGE_KEY);
  } catch {
    /* nothing to clean up */
  }
}

/** Shopper-result URL for the HyperPay widget, tagged so `/result` credits the wallet. */
export function walletTopUpResultUrl(method: WalletTopUpMethod): string {
  return `${window.location.origin}/result?type=hyperpay&flow=wallet&brand=${method}`;
}
